import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { Calendar, DateObject } from 'react-multi-date-picker';
import moment from 'moment-timezone';
import Dropdown from '../../base/Dropdown/Dropdown';
import { Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import gregorian_ru from './gregorian_ru';
import TextInput from '../TextInput/TextInput';
import './RangeDatePickerStyles.scss';
import { normalizeArrayValue } from '../../../utils/utils';
import { APP_TIMEZONE, getProjectPeriodDates, projectMoment } from '../../../utils/projectTime';

const getDateObjectFromValue = (value, periodPart) => {
  if (value && typeof value.format === 'function') {
    return value;
  }
  if (!value) {
    return null;
  }
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? moment.tz(value, 'YYYY-MM-DD', APP_TIMEZONE)
    : projectMoment(value);
  const momentValue = periodPart === 'end' ? parsed.endOf('day') : parsed.startOf('day');
  if (!momentValue.isValid()) {
    return null;
  }
  return new DateObject({ date: momentValue.format('YYYY-MM-DD'), format: 'YYYY-MM-DD' });
};

const getDateRangeFromValue = value => {
  const [startValue, endValue] = normalizeArrayValue(value);
  const start = getDateObjectFromValue(startValue, 'start');
  const end = getDateObjectFromValue(endValue, 'end');
  return start && end ? [start, end] : [];
};

export default function RangeDatePicker(props) {
  const {
    className = '',
    inline = false,
    hidePeriodButtons = false,
    inputProps = {},
    fieldState = {
      error: '',
      touched: false,
    },
    usePortal = false,
    placeholder = '',
    changeFormat = null,
    onChangeToIso = false,
    controlId = `text-input-${Math.random() * 10000}`,
  } = props;
  const { formatMessage } = useIntl();
  const { onChange = () => {}, name, value } = inputProps;
  const {
    isTouched: touched,
  } = fieldState;
  const [values, setValues] = useState([]);

  useEffect(() => {
    setValues(getDateRangeFromValue(value));
  }, [value]);

  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [pickerKey, setPickerKey] = useState(null);
  const pickerRef = useRef(null);
  const [start, end] = values;
  const onSelect = range => {
    if (range && range.length && range.length > 1) {
      setValues(range);
      const [start, end] = range;
      if (changeFormat) {
        onChange({ target: { value: [start.format(changeFormat), end.format(changeFormat)], name } });
      } else if (onChangeToIso) {
        onChange({ target: { value: start && end ? [start.toISOString(), end.toISOString()] : '', name } });
      } else {
        onChange({ target: { value: range, name } });
      }
    }
  };
  const handleChangeDate = range => {
    setSelectedPeriod(null);
    onSelect(range);
  };
  const onReset = event => {
    setValues([]);
    setSelectedPeriod('');
    onChange({ target: { value: changeFormat || onChangeToIso ? ['', ''] : '', name } });
    event.stopPropagation();
  };
  const anchorElement = useRef(null);

  const handleSelectPeriod = type => () => {
    const [start, end] = getProjectPeriodDates(type);
    setSelectedPeriod(type);
    onSelect([
      new DateObject({ date: start, format: 'YYYY-MM-DD' }),
      new DateObject({ date: end, format: 'YYYY-MM-DD' }),
    ]);
    setTimeout(() => {
      setPickerKey(pickerKey + 1);
    }, 50);
  };

  const startText = start && start.format ? start.format('DD.MM.YYYY') : '';
  const endText = end && end.format ? end.format('DD.MM.YYYY') : '';
  const isError = false;
  const textValue = (!startText || !endText) ? '' : `${startText} — ${endText}`;
  return (
    <div className={classNames('form-control-wrapper range-date-picker', { 'inline-picker': inline }, className)}>
      <div className="custom-react-date-picker">
        <Dropdown usePortal={usePortal}>
          <Dropdown.Toggle target={pickerRef && pickerRef.current ? pickerRef.current : null}>
            <div ref={anchorElement} className="date-selector-wrapper">
              <div className="date-selector">
                <TextInput
                  {...props}
                  readOnly
                  inputAfter={(
                    <div className="search-wrapper">
                      <Icons customIcon className="search" width={21} height={24} icon="calendar" />
                    </div>
                  )}
                  className={classNames('data-picker-text', { 'is-invalid': isError && touched })}
                  containerRef={pickerRef}
                  placeholder={placeholder}
                  inputProps={{
                    onChange: () => {},
                    value: textValue,
                  }}
                />
              </div>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu anchorElement={anchorElement} disableClickToggle className="data-picker-wrapper">
            <Calendar
              key={pickerKey}
              value={values}
              onChange={handleChangeDate}
              locale={gregorian_ru}
              range
              rangeHover
              weekStartDayIndex={1}
            />
            {!hidePeriodButtons
              ? (
                <div className="data-picker-filter-buttons">
                  <span
                    className={classNames('data-picker-filter-button', { active: selectedPeriod === 'yd' })}
                    onClick={handleSelectPeriod('yd')}
                  >
                    {formatMessage({ id: 'yesterday' })}
                  </span>
                  <span
                    className={classNames('data-picker-filter-button', { active: selectedPeriod === 'd' })}
                    onClick={handleSelectPeriod('d')}
                  >
                    {formatMessage({ id: 'today' })}
                  </span>
                  <span
                    className={classNames('data-picker-filter-button', { active: selectedPeriod === 'w' })}
                    onClick={handleSelectPeriod('w')}
                  >
                    {formatMessage({ id: 'week' })}
                  </span>
                  <span
                    className={classNames('data-picker-filter-button', { active: selectedPeriod === 'm' })}
                    onClick={handleSelectPeriod('m')}
                  >
                    {formatMessage({ id: 'month' })}
                  </span>
                  <span
                    className={classNames('data-picker-filter-button', { active: selectedPeriod === 'y' })}
                    onClick={handleSelectPeriod('y')}
                  >
                    {formatMessage({ id: 'year' })}
                  </span>
                  <span
                    className={classNames('data-picker-filter-button reset')}
                    onClick={onReset}
                  >
                    {formatMessage({ id: 'clear' })}
                  </span>
                </div>
              )
              : null}
          </Dropdown.Menu>
        </Dropdown>

      </div>
    </div>
  );
}
